import { Accordion } from '@tvg/fuse-design/accordion';
import { gridConfig } from '@tvg/fuse-design/responsive-grid';
import { EUserAction } from 'client/config';
import useComponentId from 'client/hooks/useComponentId';
import { usePLPNavigate } from 'client/hooks/usePLPNavigate';
import { useDrawerState } from 'client/state/Drawer';
import { useFacetListState } from 'client/state/FacetList';
import { PLPGeneralFacetValue } from 'common/types/plp-data';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { CheckboxGroupStyled, DivStyled } from '../FacetItem/FacetItem.styled';

export type FacetItemCategoryProps = {
  id: string;
  name: string;
  values?: PLPGeneralFacetValue[];
  isDrawerView?: boolean;
};
const AccordionStyled = styled(Accordion)`
  && {
    &.fuse-accordion {
      @media screen and (max-width: ${gridConfig.m.maxWidth}) {
        border-top: 0;
        border-bottom: 1px solid var(--color-global-grey400);
      }
    }
  }
`;

const TITLE_SIZE = {
  xxs: 'xsmall',
  l: 'xxsmall',
};

function FacetItemCategory({ id, name, values = [], isDrawerView = false }: FacetItemCategoryProps) {
  const plpNavigate = usePLPNavigate();
  const toggleShowViewProducts = useDrawerState((s) => s.toggleShowViewProducts);
  const { activeFacet, setActiveFacet } = useFacetListState();
  const headingName = name === 'All' ? 'Department' : `${name}`;
  const componentId = useComponentId(`facet-accordion-${id}-${name}`);

  const updateState = useCallback(
    (value: string) => {
      toggleShowViewProducts.on();
      plpNavigate({ category: value }, EUserAction.CATEGORY);
    },
    [plpNavigate, toggleShowViewProducts],
  );

  const handleChangeCallback = useCallback(
    (e: React.FormEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault(); // To avoid following the SEO URL

      const button = e.currentTarget as HTMLButtonElement;
      const value = button.dataset.value || '';

      updateState(value);
    },
    [updateState],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter') {
        e.stopPropagation();
        e.preventDefault(); // To avoid following the SEO URL
        const value = (e.target as HTMLInputElement).getAttribute('data-value');
        if (value) updateState(value);
      }
    },
    [updateState],
  );

  const onClickAccordion = useCallback(() => setActiveFacet(id || name), [id, name, setActiveFacet]);

  return (
    <AccordionStyled
      heading={headingName}
      open={(id || name) === activeFacet}
      onClick={onClickAccordion}
      dataTestId="facet-accordion"
      headingTag={isDrawerView ? 'h2' : 'h3'}
      titleSize={TITLE_SIZE}
      componentId={componentId}
    >
      <DivStyled />
      <CheckboxGroupStyled
        isShowCheckbox={false}
        groupDescription={`Filters for ${headingName}`}
        items={values}
        isFullWidth
        onChange={handleChangeCallback}
        onKeyDown={handleKeyDown}
        dataTestId="facet-button-category"
      />
    </AccordionStyled>
  );
}

export default React.memo(FacetItemCategory);
